import _ from "lodash";
import {
  dateRange,
  formatDate,
  formatMoney,
  formatNumber,
  LocaleMoneyFormat,
  LocaleNumberFormat,
} from "CommonCore/l10n";
import {
  emptyOrFalse,
  emptyOrTrue,
  setupLookups,
} from "App/shk-form-tab";
import {
  ASTMODUSKEY,
  getModusFromAntrag,
} from "App/constants";
import moment from "moment";

const MINIJOB_GRENZE_FALLBACK = 556;
const isValidMoment = value => value && value.isValid && value.isValid();
const toMomentDate = value => (value ? moment(value) : null);

const getMinijobGrenze = (stundensatzLookup, gueltigab, gueltigbis) => {
  const stundensaetze = _.values(stundensatzLookup || {});
  const ab = toMomentDate(gueltigab);
  const bis = toMomentDate(gueltigbis); // null bedeutet offene Obergrenze (infinity)

  const passenderStundensatz = _.find(stundensaetze, stundensatz => {
    const stdGueltigab = toMomentDate(stundensatz && stundensatz.std_gueltigab);
    const stdGueltigbis = toMomentDate(stundensatz && stundensatz.std_gueltigbis); // null = infinity
    if (!isValidMoment(ab) || !isValidMoment(stdGueltigab)) {
      return false;
    }
    const endsAfterRateStarts = !isValidMoment(bis) || bis.isSameOrAfter(stdGueltigab, "day");
    const startsBeforeRateEnds = !isValidMoment(stdGueltigbis) || ab.isSameOrBefore(stdGueltigbis, "day");
    return startsBeforeRateEnds && endsAfterRateStarts;
  });

  const stundensatz = passenderStundensatz || _.head(stundensaetze);
  const grenze = parseFloat(stundensatz && stundensatz.std_stundensatz);
  return Number.isFinite(grenze) ? grenze : MINIJOB_GRENZE_FALLBACK;
};

const getMinijobGrenzeText = (stundensatzLookup, gueltigab, gueltigbis) => formatNumber(
  getMinijobGrenze(stundensatzLookup, gueltigab, gueltigbis),
);

const istKeinTarifvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : antrag && getModusFromAntrag(antrag)) !== ASTMODUSKEY.Tarif,
};

const istTarifvertrag = {
  inject: ["flag_antragstellermodus", "currentAntrag"],
  generator: (modus, antrag) => () => (modus !== undefined ? modus : antrag && getModusFromAntrag(antrag)) === ASTMODUSKEY.Tarif,
};

export const schemaAlsHilfskraft = {
  table: [
    {
      field: "p_weiterebv_jn",
      display: "Ich habe bereits als Hilfskraft gearbeitet und/oder übe gleichzeitig noch eine weitere Beschäftigung aus und/oder habe in den letzten 12 Monaten eine oder mehrere befristete Beschäftigungen ausgeübt",
      lookup: "boolean.radioheader2",
      sortable: true,
    },
  ],
};

export const schemaAlsTarifangestellter = {
  table: [
    {
      field: "p_weiterebv_jn",
      display: "Ich habe bereits weitere Beschäftigungen gehabt und/oder übe gleichzeitig noch eine weitere Beschäftigung aus und/oder habe seit Jahresbeginn eine oder mehrere befristete Beschäftigungen ausgeübt",
      lookup: "boolean.radioheader2",
      sortable: true,
    },
  ],
};

export const schemaAlsLehrauftrag = {
  table: [
    {
      field: "p_weiterebv_jn",
      display: "Ich habe bereits als Lehrkraft gearbeitet und / oder übe gleichzeitig noch eine weitere Lehrtätigkeit aus",
      lookup: "boolean.radioheader2",
      sortable: true,
    },
  ],
};

export const schemaHilfskraft = {
  renderEditorRow: function(item, injects) {
    const row = [];
    const prf = Object.keys(item)[0].split("_")[0];

    row.push(<td key='ab' width='25%'>
      {`${ formatDate(item[prf + "_gueltigab"]) } - ${ item[prf + "_gueltigbis"]
        ? formatDate(item[prf + "_gueltigbis"])
        : "" }`}
    </td>);

    if (istTarifvertrag.generator(injects.flag_antragstellermodus, injects.currentAntrag)()) {
      row.push(<td key='hs'>
        {item[prf + "_arbeitgeber"]}
      </td>);
    } else {
      row.push(<td key='hs'>
        {!item[prf + "_khsid"]
          ? "(Hochschule nicht angegeben)"
          : _.get(injects.k_hochschuleLookup, [item[prf + "_khsid"], "khs_bez"], "")}
      </td>);
    }

    row.push(<td key='ag'>{item[prf + "_aufgabengebiet"]}</td>);
    row.push(<td key='row-3'>{item[prf + "_std"] ? formatNumber(parseFloat(item[prf + "_std"])) + " Std./Woche " : ""}</td>);
    return row;
  },
  table: [
    {
      field: "pwb_gueltigab",
      display: "Beginn der Tätigkeit",
      type: "date",
      required: true,
      validate: value => !value && "Bitte geben Sie den Beginn der Tätigkeit an.",
    },
    {
      field: "pwb_gueltigbis",
      validate: (value, fields) => {
        if (!value) {
          return "Bitte geben Sie das Ende der Tätigkeit an.";
        }
        if (new Date(value) <= new Date(fields.pwb_gueltigab)) {
          return "Beginn der Tätigkeit muss vor Ende der Tätigkeit liegen.";
        }
      },
      display: "Ende der Tätigkeit",
      type: "date",
      required: true,
    },
    {
      field: "pwb_std",
      display: "Wöchentlicher Zeitaufwand in Stunden",
      type: "number",
      validate: value => {
        if (!value || parseFloat(value) <= 0) {
          return "Bitte geben Sie den Zeitaufwand in Stunden als positiven numerischen Wert ein";
        }
        if (value >= 100) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert unter 100 ein";
        }
      },
      required: true,
    },
    {
      field: "pwb_khsid",
      display: "Universität",
      lookup: "k_hochschule.select",
      hideFromDetail: istTarifvertrag,
      hideFromEditor: istTarifvertrag,
      required: true,
      validate: (value, dummy, modus) => !value && modus !== ASTMODUSKEY.Tarif && "Bitte wählen Sie eine Universität aus.",
    },
    {
      field: "pwb_arbeitgeber",
      display: "Arbeitgeber",
      hideFromDetail: istKeinTarifvertrag,
      hideFromEditor: istKeinTarifvertrag,
      required: true,
      validate: (value, dummy, modus) => !value && modus === ASTMODUSKEY.Tarif && "Bitte geben Sie den Arbeitgeber an.",
    },
    {
      field: "pwb_aufgabengebiet",
      display: "Aufgabengebiet",
      maxlength: 255, // RH PBP-1921
    },
  ],
};

export const schemaLehrauftrag = {
  renderEditorRow: function(item, injects) {
    const row = [];
    const prf = Object.keys(item)[0].split("_")[0];
    row.push(<td width='25%'>{`${ formatDate(item[prf + "_gueltigab"]) } - ${ item[prf + "_gueltigbis"] ? formatDate(item[prf + "_gueltigbis"]) : "" }`}</td>);
    row.push(<td>{!item[prf + "_khsid"] ? "(Hochschule nicht angegeben)" : _.get(injects.k_hochschuleLookup, [item[prf + "_khsid"], "khs_bez"], "")}</td>);
    row.push(<td>{item[prf + "_aufgabengebiet"]}</td>);
    return row;
  },
  table: [
    {
      field: "pwb_gueltigab",
      display: "Beginn der Tätigkeit",
      type: "date",
    },
    {
      field: "pwb_gueltigbis",
      display: "Ende der Tätigkeit",
      type: "date",
    },
    {
      field: "pwb_khsid",
      display: "Universität",
      lookup: "k_hochschule.select",
    },
    {
      field: "pwb_aufgabengebiet",
      display: "Lehrauftragsbezeichnung",
      maxlength: 255,
    },
  ],
};

export const schemaAusserhalb = {
  renderEditorRow: function(item) {
    const row = [];
    const prf = Object.keys(item)[0].split("_")[0];
    row.push(<td key='row-1' width='25%'>{`${ formatDate(item[prf + "_gueltigab"]) } - ${ item[prf + "_gueltigbis"] ? formatDate(item[prf + "_gueltigbis"]) : "" }`}</td>);
    row.push(<td key='row-2'>{item[prf + "_selbst_jn"] ? "Selbstständige Tätigkeit" : item[prf + "_arbeitgeber"] || "(Arbeitgeber nicht angegeben)"}
      {item[prf + "_aufgabengebiet"] ? " / " + item[prf + "_aufgabengebiet"] : ""}
    </td>);
    row.push(<td key='row-3'>{item[prf + "_std"] ? formatNumber(parseFloat(item[prf + "_std"])) + " Std./Woche " : ""}</td>);
    row.push(<td key='row-4' style={{ textAlign: "right" }}>{formatMoney(item[prf + "_entgelt"])}</td>);
    return row;
  },
  table: [
    {
      field: "pwb_selbst_jn",
      display: "",
      lookup: {
        editType: "radioheader2",
        values: [{ value: true, label: "selbstständig" },
                 { value: false, label: "angestellt" }],
      },
      sortable: true,
      dependends: [ // Felder, die nur bei Selbständigen benötigt werden
        {
          fields: ["pwb_selbst_an_jn",
                   "pwb_selbst_hauptberuflich_jn",
                   "pwb_gruendung_jn",
                   "pwb_gewerbe_jn"],
          invisibleIf: emptyOrFalse,
          noContainer: true,
        },
        // Felder, die nur bei Angestellten benötigt werden
        {
          fields: ["pwb_gueltigbis",
                   "pwb_beamte_jn",
                   "pwb_arbeitgeber",
                   "pwb_aufgabengebiet",
                   "pwb_tage",
                   "pwb_an_versicherung_jn",
                   "pwb_an_zuschusspv_jn",
                   "pwb_rentebefreit",
                   "pwb_beurlaubt_jn"],
          invisibleIf: emptyOrTrue,
          noContainer: true,
        },
        // Felder, die beide Fälle brauchen: unsichtbar bis Fall bekannt
        {
          fields: ["pwb_inland_jn", "pwb_gueltigab", "pwb_std", "pwb_std_we", "info_arbeitszeit", "pwb_entgelt", "pwb_tage_we"],
          invisibleIf: value => value === undefined || value === null,
          noContainer: true,
        },
      ],
    },
    {
      field: "pwb_inland_jn",
      display: "",
      lookup: {
        editType: "radioheader2",
        values: [{ value: true, label: "im Inland" },
                 { value: false, label: "im Ausland" }],
      },
      dependends: [
        { fields: ["pwb_ausland_ksaid"], invisibleIf: emptyOrTrue },
      ],
    },
    {
      field: "pwb_ausland_ksaid",
      display: "Land",
      required: entity => entity.pwb_inland_jn === false,
      hideFromEditor: entity => entity.pwb_inland_jn !== false,
      lookup: "k_staat.select.ksa_id.ksa_kbez",
    },
    {
      field: "pwb_beamte_jn",
      display: "",
      lookup: {
        editType: "radioheader2",
        values: [{ value: false, label: "in einem Arbeitsverhältnis" },
                 { value: true, label: "in einem Beamtenverhältnis" }],
      },
      sortable: true,
    },
    {
      field: "pwb_arbeitgeber",
      display: "Arbeitgeber",
      maxlength: 60, // RH PBP-1921
    },
    {
      field: "pwb_aufgabengebiet",
      display: "Aufgabengebiet",
    },

    {
      field: "pwb_gueltigab",
      display: "Beginn der Tätigkeit",
      type: "date",
    },
    {
      field: "pwb_gueltigbis",
      display: "Ende der Tätigkeit",
      type: "date",
    },

    {
      field: "pwb_selbst_hauptberuflich_jn",
      display: "",
      lookup: {
        editType: "radioheader2",
        values: [{ value: true, label: "hauptberuflich" },
                 { value: false, label: "nebenberuflich" }],
      },
    },
    // -- Gründungszuschuss --
    {
      field: "pwb_gruendung_jn",
      display: "Ich erhalte einen Gründungszuschuss",
      lookup: "boolean.radioheader2",
      dependends: [
        {
          fields: ["pwb_gruendung_gueltigab", "pwb_gruendung_gueltigbis"],
          invisibleIf: emptyOrFalse,
        },
      ],
    },
    {
      field: "pwb_gruendung_gueltigab",
      display: "Beginn der Zahlung",
      type: "date",
    },
    {
      field: "pwb_gruendung_gueltigbis",
      display: "voraussichtliches Ende der Zahlung",
      type: "date",
    },

    {
      field: "pwb_gewerbe_jn",
      display: "Ich habe ein Gewerbe angemeldet",
      lookup: "boolean.radioheader2",
    },
    {
      field: "pwb_selbst_an_jn",
      display: "Ich beschäftige mindestens eine*n Arbeitnehmer*in mehr als geringfügig",
      lookup: "boolean.radioheader2",
    },
    {
      field: "pwb_std",
      display: "Wöchentlicher Zeitaufwand in Stunden",
      type: "number",
      validate: value => {
        if (value && parseFloat(value) <= 0) {
          return "Bitte geben Sie den Zeitaufwand in Stunden als positiven numerischen Wert ein";
        }
        if (value >= 100) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert unter 100 ein";
        }
      },
    },

    {
      field: "pwb_tage",
      display: "Anzahl Arbeitstage",
      type: "number",
      numeralFormat: LocaleNumberFormat,
      validate: value => {
        if (value && parseFloat(value) <= 0) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert ein";
        }
        if (value >= 100) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert unter 100 ein";
        }
      },
    },

    {
      field: "pwb_std_we",
      display: "davon Stunden am Wochenende bzw. in den Abend-/Nachtstunden",
      type: "number",
      numeralFormat: LocaleNumberFormat,
    },

    {
      field: "pwb_tage_we",
      display: "davon Tage am Wochenende bzw. in den Abend-/Nachtstunden",
      type: "number",
      numeralFormat: LocaleNumberFormat,
    },

    {
      field: "info_arbeitszeit",
      display: "<b>Hinweis:</b> Bitte unbedingt Nachweise zur Arbeitszeit beifügen, z.B. Dienstpläne, Bescheinigung des Arbeitgebers",
      type: "plaintext",
      useHeader: false,
    },

    {
      field: "pwb_entgelt",
      display: "Monatliches Arbeitseinkommen",
      type: "money",
      numeralFormat: LocaleMoneyFormat,
      inject: ["k_stundensatzLookup"],
      dependends: [
        {
          fields: ["pwb_minijob_rente_jn"],
          invisibleIf: (value, entity, injects) => {
            const minijobGrenze = getMinijobGrenze(
              injects && injects.k_stundensatzLookup,
              entity && entity.pwb_gueltigab,
              entity && entity.pwb_gueltigbis,
            );
            return value === undefined || value === null || !entity || entity.pwb_selbst_jn || parseFloat(value) > minijobGrenze;
          },
          noContainer: true,
        },
      ],
    },
    {
      field: "pwb_minijob_rente_jn",
      display: "",
      lookup: {
        editType: "radioheader2",
        values: [{ value: true, label: "mit Eigenanteil zur Rentenversicherung" },
                 { value: false, label: "ohne Eigenanteil zur Rentenversicherung" }],
      },

    },

    {
      field: "pwb_an_versicherung_jn",
      display: "Es besteht während dieser Zeit Versicherungsfreiheit in der gesetzlichen Kranken-/Pflegeversicherung?",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["pwb_an_versicherung_gueltigab"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "pwb_an_versicherung_gueltigab",
      display: "seit",
      type: "date",
    },

    {
      field: "pwb_rentebefreit",
      display: "Ich bin in dieser Zeit von der Rentenversicherungspflicht befreit?",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["pwb_versorgungswerk"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "pwb_versorgungswerk",
      display: "zugunsten Versorgungswerk",
    },

    {
      field: "pwb_an_zuschusspv_jn",
      display: "Ich erhalte von einem anderen Arbeitgeber Zuschüsse zur freiwilligen Kranken-/Pflegeversicherung",
      lookup: "boolean.radioheader2",
      dependends: [{ fields: ["pwb_an_kv"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "pwb_an_kv",
      display: "Krankenkasse, an die Zuschüsse abgeführt werden",
      sortable: true,
    },

    {
      field: "pwb_beurlaubt_jn",
      display: "Ich bin in diesem Arbeits-/Beamtenverhältnis unbezahlt beurlaubt (oder in Elternzeit)",
      lookup: "boolean.radioheader2",
      sortable: true,
      dependends: [{ fields: ["pwb_beurlaubt_gueltigab", "pwb_beurlaubt_gueltigbis"], invisibleIf: emptyOrFalse }],
    },
    {
      field: "pwb_beurlaubt_gueltigab",
      display: "beurlaubt seit",
      type: "date",
    },
    {
      field: "pwb_beurlaubt_gueltigbis",
      display: "beurlaubt bis",
      type: "date",
    },


  ],
};

export const schemaBefristet12Monate = {
  renderEditorRow: function(item, injects) {
    const row = [];
    const prf = Object.keys(item)[0].split("_")[0];
    const gueltigab = item[prf + "_gueltigab"];
    const gueltigbis = item[prf + "_gueltigbis"];
    const minijobGrenze = getMinijobGrenzeText(injects && injects.k_stundensatzLookup, gueltigab, gueltigbis);
    row.push(<td key='row-1' width='25%'>{`${ formatDate(item[prf + "_gueltigab"]) } - ${ item[prf + "_gueltigbis"] ? formatDate(item[prf + "_gueltigbis"]) : "" }`}</td>);
    row.push(<td key='row-2'>{item[prf + "_tage"] ? formatNumber(parseFloat(item[prf + "_tage"])) + " Arbeitstage " : ""}</td>);
    row.push(<td key='row-3'>{item[prf + "_std"] ? formatNumber(parseFloat(item[prf + "_std"])) + " Std./Woche " : ""}</td>);
    row.push(<td key='row-4' style={{ textAlign: "right" }}>{item[prf + "_minijob_jn"] ? `unter ${ minijobGrenze }€` : `ab ${ minijobGrenze }€` }</td>);
    return row;
  },
  table: [
    {
      field: "pwb_beamte_jn",
      display: "",
      lookup: {
        editType: "radioheader2",
        values: [{ value: false, label: "in einem Arbeitsverhältnis" },
                 { value: true, label: "in einem Beamtenverhältnis" }],
      },
      sortable: true,
      required: true,
      validate: value => {
        if (typeof value !== "boolean") {
          return "Bitte angeben";
        }
      },
    },
    {
      field: "pwb_gueltigab",
      display: "Beginn der Tätigkeit",
      type: "date",
      required: true,
      validate: value => {
        if (!value) {
          return "Bitte angeben";
        }
      },
    },
    {
      field: "pwb_gueltigbis",
      display: "Ende der Tätigkeit",
      type: "date",
      required: true,
      validate: value => {
        if (!value) {
          return "Bitte angeben";
        }
      },
    },
    {
      field: "pwb_tage",
      display: "Tatsächliche Arbeitstage in diesem Zeitraum (inkl. Urlaubstage)",
      type: "number",
      numeralFormat: LocaleNumberFormat,
      hideFromEditor: entity => {
        const MIN_MONATE = 3;
        if (entity && entity.pwb_gueltigab && entity.pwb_gueltigbis) {
          const monate = Math.abs(new moment(entity.pwb_gueltigab).diff(new moment(entity.pwb_gueltigbis), "months"));
          // // Da Math.abs muss hier auf größe, gleich abgefragt werden.
          return monate >= MIN_MONATE;
        }
        return true;
      },
      validate: value => {
        if (!value || parseFloat(value) <= 0) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert ein";
        }
        if (value >= 100) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert unter 100 ein";
        }
      },
    },
    {
      field: "pwb_std",
      display: "Wöchentliche Arbeitszeit (in Stunden)",
      type: "number",
      required: true,
      validate: value => {
        if (!value || parseFloat(value) <= 0) {
          return "Bitte geben Sie den Zeitaufwand in Stunden als positiven numerischen Wert ein";
        }
        if (value >= 100) {
          return "Bitte geben Sie den Zeitaufwand in Tagen als positiven numerischen Wert unter 100 ein";
        }
      },
    },
    {
      field: "pwb_minijob_jn",
      display: "Monatliches Bruttoarbeitsentgelt (einschl. anteiliger Einmalzahlungen)",
      inject: ["k_stundensatzLookup"],
      lookup: {
        settings: {
          type: "radioheader2",
          inject: ["k_stundensatzLookup"],
          choices: k_stundensatzLookup => entity => {
            const minijobGrenze = getMinijobGrenzeText(k_stundensatzLookup, entity && entity.pwb_gueltigab, entity && entity.pwb_gueltigbis);
            return [{ value: true, label: `Unter ${ minijobGrenze } €` },
                    { value: false, label: `Ab ${ minijobGrenze } €` }];
          },
          selector: k_stundensatzLookup => (value, entity) => {
            const minijobGrenze = getMinijobGrenzeText(k_stundensatzLookup, entity && entity.pwb_gueltigab, entity && entity.pwb_gueltigbis);
            if (value === true) {
              return `Unter ${ minijobGrenze } €`;
            }
            if (value === false) {
              return `Ab ${ minijobGrenze } €`;
            }
            return "";
          },
        },
      },
      sortable: true,
      validate: value => {
        if (typeof value !== "boolean") {
          return "Bitte angeben";
        }
      },
    },

  ],
};


setupLookups(schemaAlsHilfskraft);
setupLookups(schemaAlsTarifangestellter);
setupLookups(schemaAlsLehrauftrag);
schemaHilfskraft.table = setupLookups(schemaHilfskraft);
schemaLehrauftrag.table = setupLookups(schemaLehrauftrag);
schemaAusserhalb.table = setupLookups(schemaAusserhalb);
schemaBefristet12Monate.table = setupLookups(schemaBefristet12Monate);

export const weitereBVRenderMethods = {
  mfi_prozent: (value, obj) => `${ formatNumber(obj.mfi_prozent || 0) }%`,
  mfi_kostenstelle: (value, obj) => `${ obj.mfi_kostenstelle }${ obj.mfi_budgetkonto }`,
  dateRange: (value, obj) => dateRange(obj.mfi_gueltigab, obj.mfi_gueltigbis),
};

export const saveWeitereBV = changeCallback => (entity, data, arrayinfo) => {
  // Vorherigen Stand des Detail-Arrays ermitteln
  const array = entity[arrayinfo.property] || [];

  if (_.isEmpty(data)) {
    // Nichts zu tun
  } else {
    // Wenn Daten eine ID enthalten, muss der Satz im bestehenden Array aktualisiert werden
    if (data[arrayinfo.id]) {
      // ID gefüllt, Satz suchen
      const idx = array.findIndex(x => x[arrayinfo.id] == data[arrayinfo.id]);
      if (idx >= 0) {
        // Record gefunden, aktualisieren
        _.assign(array[idx], data);
      } else {
        // Nicht gefunden (?), anhängen
        array.push(data);
      }
    } else {
      array.push(data);
    }
  }
  entity[arrayinfo.property] = array;
  changeCallback && changeCallback(arrayinfo.property, array);
};


/*
 *
 *{ field: 'info_niedriglohn',
 *display: 'In der Sozialversicherung sind bestimmte Beschäftigungen besonders zu prüfen. Hierbei handelt es sich um<br />'
 *        + '<ol type=a>'
 *        + '<li>geringfügig entlohnte Beschäftigungen,'
 *        + ' bei denen das regelmäßige monatliche Einkommen den Betrag von 450,00 Euro nicht überschreitet (dabei werden jährliche'
 *        + ' Einmalzahlungen anteilig mit berücksichtigt)</li>'
 *        + '<li>Beschäftigungen innerhalb  einer  einkommensbezogenen  Gleitzone, bei denen das regelmäßige monatliche Einkommen zwischen'
 *        + ' 450,01 EUR und 850,00 EUR liegt</li>'
 *        + '<li>kurzfristige Beschäftigungen,'
 *        + ' die - unabhängig von der Höhe des Einkommens - innerhalb  eines  Kalenderjahres  auf  nicht  mehr  als  drei  Monate  oder'
 *        + ' 70 Arbeitstage begrenzt sind</li>'
 *        + '<ol>',
 *type: 'plaintext',
 *useHeader: false,
 *subtag: 'niedriglohn',
 *},
 *
 */
